import Vue from "vue";
import router from "@/router";
import store from "@/store";
import axios from "axios";
import { getQueryString, getCookie } from "@/utils";
import { isURL } from "./validate";
import http from "./httpRequest";

const loginHttp = axios.create({
  timeout: 1000 * 30,
  withCredentials: true,
  baseURL:
    process.env.NODE_ENV !== "production" && process.env.OPEN_PROXY
      ? "/api"
      : process.env.VUE_APP_proxybaseurl + "/api",
  headers: {
    "X-Requested-With": "XMLHttpRequest",
    X_PROXY_GATEWAY: true,
    "Content-Type": "application/json; charset=utf-8",
    "Cache-Control": "no-cache",
    access_token: localStorage.getItem("token") || "",
    ["Pragma"]: "no-cache",
  },
});

/**
 * 请求拦截
 */
loginHttp.interceptors.request.use((config) => {
  config.headers["access_token"] =
    getCookie("access_token") || sessionStorage.getItem("access_token"); // 请求头带上token
  return config;
});

/**
 * 响应拦截
 */
loginHttp.interceptors.response.use(
  (response) => {
    if (
      response.data &&
      (response.data.code === 401 || response.data.code === "1003")
    ) {
      // 401, token失效
      logout(false);
    }
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * 是否有权限
 * @param {*} key
 */
export function isAuth(key) {
  return (
    JSON.parse(sessionStorage.getItem("permissions") || "[]").indexOf(key) !==
      -1 || false
  );
}

/**
 * 清除登录信息
 */
export function clearLoginInfo() {
  store.commit("resetStore");
  router.options.isAddDynamicMenuRoutes = false;
}

/**
 *
 * @param shouldCallLogout 是否需要调用内置的后端退出方法,用于清理Session
 */
export function logout(shouldCallLogout) {
  if (shouldCallLogout === false) {
    //调用退出接口清理缓存信息
    clearLoginInfo()
    if(process.env.VUE_APP_ssoLogin == 'true'){
      redirectSsoLoginUrl(function (data) { },true)
    }else{
      router.push({ name: 'login' })
    }
  } else {
    logoutInternal(function () {
      clearLoginInfo();
      if(process.env.VUE_APP_ssoLogin == 'true'){
        redirectSsoLoginUrl(function (data) { },true)
      }else{
        router.push({ name: 'login' })
      }
    });
  }
}

function logoutInternal(callback) {
  let url = "/logout";
  loginHttp({
    url: url,
    method: "get",
  })
    .then(({ data }) => {
      if (callback !== undefined) {
        callback(data);
      } else {
        console.log(data);
      }
    })
    .catch((e) => {
      if (callback !== undefined) {
        callback({ code: "1001", msg: "系统异常" });
      } else {
        console.log(e);
      }
    });
}

export function logoutWithoutRedirect(callback) {
  logoutInternal(function () {
    clearLoginInfo();
    if (callback !== undefined) {
      callback();
    }
  });
}

export function redirectSsoLoginUrl(next, isLogout) {
  if (router.ssoLoginUrl) {
    location.href = fillLogoutInfoToUrl(router.ssoLoginUrl, isLogout);
  } else {
    getSimpleUserInfo(function (data) {
      if (data.code === "1" && data.ssoServerUrl) {
        router.ssoLoginUrl = data.ssoServerUrl;
        location.href = fillLogoutInfoToUrl(router.ssoLoginUrl, isLogout);
      } else {
        if (next !== undefined) {
          next({ name: "error", params: { msg: data.msg } });
        } else {
          router.push({ name: "error", params: { msg: data.msg } });
        }
      }
    });
  }
}

function fillLogoutInfoToUrl(url, isLogout) {
  if (isLogout === true && url) {
    if (url.indexOf("?") >= 0) {
      url = url + "&logout=1";
    } else {
      url = url + "?logout=1";
    }
  }
  let oauth = getQueryString("oauth");
  if (oauth && oauth !== "undefined" && oauth !== "null") {
    if (url.indexOf("?") >= 0) {
      url = url + "&oauth=" + oauth;
    } else {
      url = url + "?oauth=" + oauth;
    }
  }
  return url;
}

export function redirectToApp(app) {
  let url = app.webInfo;
  if (url === undefined || url === "" || !isURL(url)) {
    router.push({ name: "error", params: { msg: "应用地址无效" } });
    return;
  }
  if (url.indexOf("?") >= 0) {
    url = url + "&oauth=2";
  } else {
    url = url + "?oauth=2";
  }
  location.href = url;
}

export function checkLogin(next) {
  let userId = store.state.user.id;
  if (!userId || !/\S/.test(userId)) {
    checkLoginByBasicInfo(next);
    return;
  }
  //当前登录状态
  if (next !== undefined) {
    next();
  }
}

/**
 * 先判断用户是否登录
 * @param next
 */
function checkLoginByBasicInfo(next) {
  loadUserInfo(function (result) {
    fillUserInfo(result);
    if (result && result.code === "1") {
      //用户信息重新加载时必须重新加载菜单
      if (next !== undefined) {
        next(result);
      }
    } else {
      clearLoginInfo()
      if(process.env.VUE_APP_ssoLogin == 'true'){
        redirectSsoLoginUrl(next)
      }else{
        router.push({ name: 'login' })
      }
    }
  });
}

export function getSimpleUserInfo(callback) {
  let url = "/userinfo/simple";
  loginHttp
    .get(url)
    .then(({ data }) => {
      if (callback !== undefined) {
        callback(data);
      } else {
        console.log(data);
      }
    })
    .catch((e) => {
      if (callback !== undefined) {
        callback({ code: "1001", msg: "系统异常" });
      } else {
        console.log(e);
      }
    });
}

export function loadUserInfo(callback) {
  let url = "/userinfo";
  loginHttp
    .get(url)
    .then(({ data }) => {
      fillUserInfo(data);
      if (callback !== undefined) {
        callback(data);
      } else {
        console.log(data);
      }
    })
    .catch((e) => {
      if (callback !== undefined) {
        callback({ code: "1001", msg: "系统异常" });
      } else {
        console.log(e);
      }
    });
}

export function fillUserInfo(data) {
  if (!router.ssoLoginUrl && data && data.ssoServerUrl) {
    router.ssoLoginUrl = data.ssoServerUrl;
  }
  if (data && data.code === "1") {
    let internalRoleIds = data.data.internalRoleIds;
    if (internalRoleIds === undefined) {
      internalRoleIds = [];
    }

    // 游客登录
    if(data.data.guest){
      clearLoginInfo()
      if(process.env.VUE_APP_ssoLogin == 'true'){
        redirectSsoLoginUrl(function (data) { },true)
      }else{
        router.push({ name: 'login' })
      }
    }

    store.commit("user/updateId", data.data.userId);
    store.commit("user/updateUser", data.data);
    store.commit("user/updateDisplayName", data.data.displayName);
    store.commit("user/updateName", data.data.username);
    store.commit("user/updateSupperAdmin", data.data.supperAdmin);

    store.commit("user/updateOrgInfo", data.data.orgInfo);
    store.commit("user/updateOrgId", data.data.orgId);
    store.commit("user/updateEntId", data.data.entId);
    store.commit(
      "user/updateSelectInternalRoleId",
      data.data.selectInternalRoleId
    );
    store.commit("user/updateInternalRoleIds", internalRoleIds);
  } else {
    store.commit("user/updateId", "");
    store.commit("user/updateUser", {});
    store.commit("user/updateDisplayName", "");
    store.commit("user/updateName", "");
    store.commit("user/updateSupperAdmin", false);

    store.commit("user/updateOrgInfo", {});
    store.commit("user/updateOrgId", "");
    store.commit("user/updateEntId", "");
    store.commit("user/updateSelectInternalRoleId", "");
    store.commit("user/updateInternalRoleIds", []);

    // 获取登录信息失败或者没有登录人信息 跳转登录入口
    if(process.env.VUE_APP_ssoLogin == 'true'){
      redirectSsoLoginUrl(function (data) { },true)
    }else{
      router.push({ name: 'login' })
    }

  }
}

export default loginHttp;
